/**
 * useArrivalVerification — Ted #28 (#1050). The one queue behind the
 * arrival-verify dialog, shared by every check-in surface.
 *
 * A surface calls `ask(request)` when the server answers a check-in with
 * 409 `arrival_unverified`; requests are answered ONE AT A TIME, in order,
 * so a household "Check in all" with two transported dogs asks twice. The
 * hook owns the dialog element; the surface renders `dialog` once, anywhere
 * in its tree.
 */
import { useCallback, useState } from 'react';
import { ArrivalVerifyDialog, type ArrivalVerifyRequest } from './ArrivalVerifyDialog.js';

export interface ArrivalVerification {
  /** Queue a confirmation. Duplicate booking ids collapse onto the first. */
  ask: (request: ArrivalVerifyRequest) => void;
  /** True while a question is on screen. */
  pending: boolean;
  /** The dialog for the request at the head of the queue, or null. */
  dialog: JSX.Element | null;
}

export function useArrivalVerification(): ArrivalVerification {
  const [queue, setQueue] = useState<ArrivalVerifyRequest[]>([]);

  const ask = useCallback((request: ArrivalVerifyRequest) => {
    setQueue((curr) =>
      curr.some((r) => r.bookingId === request.bookingId) ? curr : [...curr, request],
    );
  }, []);

  const shift = useCallback(() => {
    setQueue((curr) => curr.slice(1));
  }, []);

  const head = queue[0] ?? null;
  const dialog = head ? (
    <ArrivalVerifyDialog
      key={head.bookingId}
      request={head}
      onConfirm={() => {
        shift();
        void head.onConfirm();
      }}
      onCancel={() => {
        shift();
        head.onCancel?.();
      }}
    />
  ) : null;

  return { ask, pending: head !== null, dialog };
}
